import { useCallback, useEffect, useState } from 'react'
import Dropzone from 'react-dropzone'
import { useHistory, useLocation } from 'react-router-dom'

import DescriptionIcon from '@mui/icons-material/Description'
import GetAppIcon from '@mui/icons-material/GetApp'
import PublishIcon from '@mui/icons-material/Publish'
import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { useMap } from '@react-hookz/web'
import { useMutation } from '@tanstack/react-query'
import Cookie from 'js-cookie'
import { useSnackbar } from 'notistack'

import FixedBox from '~/components/FixedBox'
import { updateStockItems } from '~/products/api/updateStockItems'
import { updateStockRecords } from '~/products/api/updateStockRecords'
import StockItemFilter from '~/stock/components/stock-item-filter'
import StockManagerTableNew, {
    Props as StockManagerTableProps,
} from '~/stock/components/stock-manager-table'
import { useAppSelector } from '~/store'

type StoreSelectorProps = {
    selectedStoreId: number
    onChange: (storeId: number) => void
}

function StoreSelector({ selectedStoreId, onChange }: StoreSelectorProps) {
    const stores = useAppSelector((state) => state.initial.stores)

    return (
        <FormControl fullWidth size="small">
            <Select
                displayEmpty
                color="secondary"
                value={selectedStoreId || ''}
                onChange={(e) => onChange(Number(e.target.value))}
            >
                <MenuItem disabled value="">
                    Select store
                </MenuItem>
                {stores.map((store) => (
                    <MenuItem key={store.id} value={store.id}>
                        {store.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}

const UPDATE_MODES = {
    ADD: 'add',
    DEDUCT: 'deduct',
    UPDATE: 'update',
} as const
type UpdateMode = (typeof UPDATE_MODES)[keyof typeof UPDATE_MODES]

const UPDATE_MODE_LABELS = {
    [UPDATE_MODES.ADD]: gettext('Add Stock'),
    [UPDATE_MODES.DEDUCT]: gettext('Deduct Stock'),
    [UPDATE_MODES.UPDATE]: gettext('Update Stock'),
} as const

const UPDATE_MODE_EXPLANATIONS = {
    [UPDATE_MODES.ADD]: gettext(
        'To add stock, download the template CSV file and enter the number of units you want to add to existing stock for each SKU.'
    ),
    [UPDATE_MODES.DEDUCT]: gettext(
        'To deduct stock, download the template CSV file and enter the number of units you want to deduct from existing stock for each SKU.'
    ),
    [UPDATE_MODES.UPDATE]: gettext(
        'To update stock, download the template CSV file and enter the exact new stock level for each SKU.'
    ),
} as const

type StockDialogProps = {
    open: boolean
    onClose: () => void
}

function BulkUpdateManager({ open, onClose }: StockDialogProps) {
    const { updateStockAPIURL, stockForStoreURL } = useAppSelector(
        (state) => state.initial.endpoints
    )

    const [updateMode, setUpdateMode] = useState<UpdateMode>(UPDATE_MODES.ADD)
    const [uploadSummary, setUploadSummary] = useState<{
        not_found_rows: string[]
        error_rows: string[]
        successful_updates: number
    } | null>(null)
    const [selectedStoreId, setSelectedStoreId] = useState<number>(0)
    const [fileToUpload, setFileToUpload] = useState<File | null>(null)
    const [uploading, setUploading] = useState(false)

    function handleDownloadTemplate() {
        if (!selectedStoreId) {
            return
        }

        const url = stockForStoreURL.replace('store_pk', selectedStoreId.toString())
        window.location.href = `${url}?update_mode=${updateMode}`
    }

    function handleUpload() {
        setUploading(true)
        setUploadSummary(null)
    }

    useEffect(() => {
        if (!uploading || !selectedStoreId || !fileToUpload?.type) {
            return
        }

        window.analytics.track('Upload stock sheet', {
            page: window.location.href,
            feature: 'stock',
            sub_feature: 'update',
            type: 'bulk update',
            trigger: 'button click',
            store_id: selectedStoreId,
            update_mode: updateMode,
        })

        const url = updateStockAPIURL
            .replace('store_pk', selectedStoreId.toString())
            .replace('update_mode', updateMode)
        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': fileToUpload.type,
                'X-CSRFToken': Cookie.get('csrftoken') ?? '',
            },
            body: fileToUpload,
        }).then((response) => {
            if (response.ok) {
                response.json().then((data) => {
                    setUploading(false)
                    setUploadSummary(data.summary)
                    setFileToUpload(null)
                })
            } else {
                response.json().then((data) => {
                    setUploading(false)
                    setUploadSummary(data.summary)
                })
            }
        })
    }, [fileToUpload, selectedStoreId, uploading, updateMode, updateStockAPIURL])

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={open}
            TransitionProps={{
                onExited: () => {
                    setUploadSummary(null)
                    setSelectedStoreId(0)
                },
            }}
        >
            <Tabs
                centered
                textColor="secondary"
                indicatorColor="secondary"
                value={updateMode}
                onChange={(_, value: UpdateMode) => setUpdateMode(value)}
            >
                <Tab value={UPDATE_MODES.ADD} label={UPDATE_MODE_LABELS[UPDATE_MODES.ADD]} />
                <Tab value={UPDATE_MODES.DEDUCT} label={UPDATE_MODE_LABELS[UPDATE_MODES.DEDUCT]} />
                <Tab value={UPDATE_MODES.UPDATE} label={UPDATE_MODE_LABELS[UPDATE_MODES.UPDATE]} />
            </Tabs>
            <DialogTitle>
                {interpolate(gettext('Bulk %s'), [UPDATE_MODE_LABELS[updateMode]])}
            </DialogTitle>
            <DialogContent>
                {uploadSummary ? (
                    <Stack
                        sx={{
                            border: (theme) => `1px solid ${theme.palette.grey[500]}`,
                            borderRadius: 1,
                            padding: 2.5,
                            textAlign: 'center',
                        }}
                    >
                        <Typography fontWeight={500}>{gettext('Update summary')}</Typography>
                        <Typography>
                            {interpolate(gettext('Updated stock for %s stock units'), [
                                uploadSummary.successful_updates,
                            ])}
                        </Typography>
                        {uploadSummary.successful_updates === 0 && (
                            <Typography>{gettext('No quantities changed')}</Typography>
                        )}
                        {uploadSummary.not_found_rows.length > 0 && (
                            <Typography color="error">
                                {interpolate(
                                    ngettext(
                                        'The SKU in row %s was not found for the store and location you specified',
                                        'The SKUs in rows %s were not found for the store and location you specified',
                                        uploadSummary.not_found_rows.length
                                    ),
                                    [uploadSummary.not_found_rows.join(', ')]
                                )}
                            </Typography>
                        )}
                        {uploadSummary.error_rows.length > 0 && (
                            <Typography color="error">
                                {interpolate(
                                    gettext(
                                        'The item in rows %s were not updated because of errors. Check the quantity you entered.'
                                    ),
                                    [uploadSummary.error_rows.join(', ')]
                                )}
                            </Typography>
                        )}
                    </Stack>
                ) : (
                    <Stack gap={1}>
                        <DialogContentText>
                            {UPDATE_MODE_EXPLANATIONS[updateMode]}
                        </DialogContentText>
                        <StoreSelector
                            selectedStoreId={selectedStoreId}
                            onChange={setSelectedStoreId}
                        />
                        <Button
                            color="secondary"
                            variant="outlined"
                            startIcon={<GetAppIcon />}
                            size="small"
                            disabled={!selectedStoreId}
                            onClick={handleDownloadTemplate}
                        >
                            {interpolate(gettext('Download %s Template'), [
                                UPDATE_MODE_LABELS[updateMode],
                            ])}
                        </Button>
                        <Dropzone
                            maxSize={1000000}
                            accept="text/csv,text/plain,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/x-csv," // comma at end implies empty string; file type may be empty string on Windows
                            onDropAccepted={(files) => setFileToUpload(files[0])}
                        >
                            {({ getRootProps, getInputProps, isDragActive }) => (
                                <Stack
                                    gap={1}
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                        height: 100,
                                        backgroundColor: (theme) =>
                                            isDragActive
                                                ? theme.palette.grey[400]
                                                : theme.palette.grey[100],
                                        cursor: 'pointer',
                                        outline: 'none',
                                        borderRadius: (theme) => theme.shape.borderRadius,
                                        overflow: 'hidden',
                                    }}
                                    {...getRootProps()}
                                >
                                    <input {...getInputProps()} />
                                    {isDragActive ? (
                                        <Typography variant="body2">
                                            {gettext('Drop file here')}
                                        </Typography>
                                    ) : (
                                        <>
                                            <DescriptionIcon color="action" />
                                            <Typography variant="body2">
                                                {gettext('Drop file here or click to select')}
                                            </Typography>
                                        </>
                                    )}
                                </Stack>
                            )}
                        </Dropzone>
                        {fileToUpload && (
                            <Stack
                                direction="row"
                                justifyContent="space-around"
                                alignItems="center"
                                paddingBlock={2}
                            >
                                <Typography>{fileToUpload.name}</Typography>
                                <LoadingButton
                                    variant="outlined"
                                    color="secondary"
                                    loading={uploading}
                                    disabled={!selectedStoreId}
                                    startIcon={<PublishIcon />}
                                    onClick={handleUpload}
                                >
                                    {gettext('Upload')}
                                </LoadingButton>
                            </Stack>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    {gettext('Close')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function DownloadManager({ open, onClose }: StockDialogProps) {
    const { stockForStoreURL } = useAppSelector((state) => state.initial.endpoints)

    const [selectedStoreId, setSelectedStoreId] = useState(0)

    const handleDownloadStock = () => {
        if (!selectedStoreId) {
            return
        }

        window.location.href = stockForStoreURL.replace('store_pk', selectedStoreId.toString())
    }

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={open}
            TransitionProps={{
                onExited: () => setSelectedStoreId(0),
            }}
        >
            <DialogTitle>{gettext('Download stock data')}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ width: '100%' }}>
                    <StoreSelector
                        selectedStoreId={selectedStoreId}
                        onChange={setSelectedStoreId}
                    />
                    <Button
                        color="secondary"
                        variant="outlined"
                        startIcon={<GetAppIcon />}
                        disabled={!selectedStoreId}
                        onClick={handleDownloadStock}
                    >
                        {gettext('Download stock for store')}
                    </Button>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={onClose}>
                    {gettext('Close')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

type StockItem = {
    id: number
    name?: string
    sku?: string
    buffer_threshold?: number
    barcode?: string | null
    stockRecords?: Array<{ id: number; quantity: number; location: number }>
}

const useUpdateStockMutation = () => {
    const { bulkUpdateStockItemsAPIURL, updateStockRecordsAPIURL } = useAppSelector(
        (state) => state.initial.endpoints
    )

    return useMutation({
        mutationFn: async (stockItemsToUpdate: StockItem[]) => {
            const promises: Promise<{ count: number }>[] = []

            const stockItems = stockItemsToUpdate
                .filter((item) => item.barcode || item.buffer_threshold || item.name)
                .map(({ stockRecords: _stockRecords, ...item }) => ({ ...item }))

            if (stockItems.length) {
                promises.push(updateStockItems(bulkUpdateStockItemsAPIURL, stockItems))
            }

            const stockRecords = stockItemsToUpdate.flatMap((stockItem) => {
                return (
                    stockItem.stockRecords?.map((record) => ({
                        ...record,
                        stock_item: {
                            id: stockItem.id,
                            sku: stockItem.sku,
                        },
                        location: {
                            id: record.location,
                        },
                    })) ?? []
                )
            })

            if (stockRecords.length) {
                promises.push(updateStockRecords(updateStockRecordsAPIURL, stockRecords))
            }

            return await Promise.all(promises)
        },
    })
}

export default function StockManagerPage() {
    const history = useHistory()
    const location = useLocation()

    const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false)
    const handleCloseBulkUpdateDialog = useCallback(() => {
        setBulkUpdateOpen(false)
    }, [])

    const [downloadOpen, setDownloadOpen] = useState(false)
    const handleCloseDownloadDialog = useCallback(() => {
        setDownloadOpen(false)
    }, [])

    const stockItemsToUpdate = useMap<StockItem['id'], StockItem>()
    const handleQuantityChange: Required<StockManagerTableProps>['onQuantityChange'] = useCallback(
        (updatedRecord) => {
            const stockItem = stockItemsToUpdate.get(updatedRecord.stockItem.id)
            if (!stockItem) {
                stockItemsToUpdate.set(updatedRecord.stockItem.id, {
                    id: updatedRecord.stockItem.id,
                    stockRecords: [
                        {
                            id: updatedRecord.id,
                            quantity: updatedRecord.quantity,
                            location: updatedRecord.location,
                        },
                    ],
                })
                return
            }

            const stockRecords = stockItem.stockRecords ?? []
            const existingRecord = stockRecords.find(
                (record) => record.location === updatedRecord.location
            )
            if (existingRecord) {
                existingRecord.quantity = updatedRecord.quantity
            } else {
                stockRecords.push({
                    id: updatedRecord.id,
                    quantity: updatedRecord.quantity,
                    location: updatedRecord.location,
                })
            }

            stockItemsToUpdate.set(updatedRecord.stockItem.id, {
                ...stockItem,
                stockRecords,
            })
        },
        [stockItemsToUpdate]
    )

    const handleStockItemChange: Required<StockManagerTableProps>['onStockItemChange'] =
        useCallback(
            ({ stockItemId, key, value }) => {
                const stockItem = stockItemsToUpdate.get(stockItemId)
                if (!stockItem) {
                    stockItemsToUpdate.set(stockItemId, { id: stockItemId, [key]: value })
                    return
                }

                stockItemsToUpdate.set(stockItemId, { ...stockItem, [key]: value })
            },
            [stockItemsToUpdate]
        )

    const { enqueueSnackbar } = useSnackbar()
    const { isLoading, mutate } = useUpdateStockMutation()

    function handleSave() {
        if (!stockItemsToUpdate.size) {
            enqueueSnackbar(gettext('No changes to save'), { variant: 'error' })
            return
        }

        const stockItems = Array.from(stockItemsToUpdate.values())
        mutate(stockItems, {
            onSuccess: () => {
                enqueueSnackbar(gettext('Saved success'), { variant: 'success' })
                stockItemsToUpdate.clear()
            },
            onError: () => {
                enqueueSnackbar(gettext('Saved failed'), { variant: 'error' })
            },
        })
    }

    return (
        <>
            <FixedBox
                component={Paper}
                sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
                <Stack direction="row" justifyContent="space-between" alignContent="center">
                    <StockItemFilter>
                        <FormControlLabel
                            checked={location.search.includes('show_reserved_stock_issues=True')}
                            componentsProps={{
                                typography: {
                                    variant: 'body2',
                                },
                            }}
                            label={gettext('Show only reserved stock issues')}
                            control={
                                <Checkbox
                                    size="small"
                                    color="secondary"
                                    onChange={(_, checked) => {
                                        const search = new URLSearchParams(location.search)
                                        if (checked) {
                                            search.set('show_reserved_stock_issues', 'True')
                                        } else {
                                            search.delete('show_reserved_stock_issues')
                                        }

                                        history.replace({ search: search.toString() })
                                    }}
                                />
                            }
                        />
                    </StockItemFilter>

                    <Box>
                        <Button
                            startIcon={<PublishIcon />}
                            color="secondary"
                            onClick={() => {
                                setBulkUpdateOpen(true)
                            }}
                        >
                            {gettext('Bulk update')}
                        </Button>
                        <Button
                            startIcon={<GetAppIcon />}
                            color="secondary"
                            onClick={() => {
                                setDownloadOpen(true)
                            }}
                        >
                            {gettext('Download')}
                        </Button>
                    </Box>
                </Stack>

                <Box sx={{ flex: 1 }}>
                    <StockManagerTableNew
                        onQuantityChange={handleQuantityChange}
                        onStockItemChange={handleStockItemChange}
                    />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        disabled={isLoading}
                        color="secondary"
                        variant="contained"
                        onClick={handleSave}
                    >
                        {gettext('Save')}
                    </Button>
                </Box>
            </FixedBox>

            <BulkUpdateManager open={bulkUpdateOpen} onClose={handleCloseBulkUpdateDialog} />
            <DownloadManager open={downloadOpen} onClose={handleCloseDownloadDialog} />
        </>
    )
}
